/**
 * Firebase Config Management Component
 *
 * Super Admin only — manages per-merchant Firebase credentials.
 * Credentials are never shown; only projectId and status are displayed.
 */

import React, { useEffect, useRef, useState } from "react";
import { AppCard } from "../../components/ui";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { confirmDialog } from "primereact/confirmdialog";
import Loader from "../../components/Loader";
import { firebaseConfigApiService } from "../../services/api/notifications";
import { FirebaseConfigDto } from "../../models/notifications";

interface FirebaseConfigProps {
    merchantId: number;
}

const FirebaseConfig: React.FC<FirebaseConfigProps> = ({ merchantId }) => {
    const toast = useRef<Toast>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [config, setConfig] = useState<FirebaseConfigDto | null>(null);

    useEffect(() => {
        loadConfig();
    }, [merchantId]);

    const loadConfig = async () => {
        setIsLoading(true);
        const result = await firebaseConfigApiService.get(merchantId);
        if (result.success) {
            setConfig(result.data);
        } else {
            // 404 = not configured, which is a valid state
            setConfig(null);
        }
        setIsLoading(false);
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Read the file content immediately before any state changes
        // to avoid re-render issues with the file input
        let text: string;
        try {
            text = await file.text();
        } catch {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to read file",
                life: 4000,
            });
            return;
        }

        // Now safe to update state — file is already read
        setIsUploading(true);

        // Validate it's valid JSON
        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(text);
        } catch {
            toast.current?.show({
                severity: "error",
                summary: "Invalid File",
                detail: "The file is not valid JSON. Please upload a Firebase service account JSON file.",
                life: 4000,
            });
            setIsUploading(false);
            return;
        }

        // Extract projectId
        const projectId = (parsed.project_id as string) ?? undefined;

        // Base64 encode
        const credentialsJson = btoa(text);

        const result = await firebaseConfigApiService.createOrUpdate({
            merchantId,
            credentialsJson,
            projectId,
        });

        if (result.success) {
            toast.current?.show({
                severity: "success",
                summary: "Uploaded",
                detail: `Firebase credentials ${config ? "updated" : "configured"} successfully${projectId ? ` (project: ${projectId})` : ""}`,
                life: 3000,
            });
            loadConfig();
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to upload Firebase credentials",
                life: 4000,
            });
        }

        setIsUploading(false);
    };

    const handleDelete = () => {
        confirmDialog({
            message: "Are you sure you want to remove Firebase credentials for this merchant? Push notifications will stop working until new credentials are uploaded.",
            header: "Remove Firebase Credentials",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                const result = await firebaseConfigApiService.delete(merchantId);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Removed",
                        detail: "Firebase credentials have been removed",
                        life: 3000,
                    });
                    setConfig(null);
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to remove Firebase credentials",
                        life: 4000,
                    });
                }
            },
        });
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            <Toast ref={toast} position="bottom-center" />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Status Card */}
                <AppCard>
                    <AppCard.Header>
                        <div className="flex flex-col gap-1">
                            <span>Firebase Configuration</span>
                            <small className="text-neutral-500 font-normal">
                                Per-merchant Firebase Cloud Messaging credentials for push notifications.
                            </small>
                        </div>
                    </AppCard.Header>
                    <AppCard.Body>
                        {config ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <Tag
                                        value={config.isActive ? "Active" : "Inactive"}
                                        severity={config.isActive ? "success" : "warning"}
                                    />
                                    <span className="text-sm text-gray-600">
                                        Merchant ID: {config.merchantId}
                                    </span>
                                </div>

                                <div>
                                    <span className="text-sm text-gray-600 font-semibold">Project ID:</span>
                                    <div className="font-mono text-sm bg-slate-50 p-2 rounded mt-1">
                                        {config.projectId || <span className="text-gray-400 italic">Not specified</span>}
                                    </div>
                                </div>

                                <div>
                                    <span className="text-sm text-gray-600 font-semibold">Configured:</span>
                                    <div className="text-sm text-gray-700 mt-1">
                                        {new Date(config.createdAt).toLocaleDateString()}
                                        {config.updatedAt && (
                                            <span className="text-gray-500">
                                                {" "}(updated: {new Date(config.updatedAt).toLocaleDateString()})
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-2">
                                    <Button
                                        size="small"
                                        label="Remove Credentials"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        outlined
                                        onClick={handleDelete}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 bg-amber-50 rounded border border-amber-200 text-center">
                                <i className="pi pi-exclamation-triangle text-amber-500 text-2xl mb-2" />
                                <div className="font-semibold text-amber-700">Not Configured</div>
                                <small className="text-amber-600">
                                    Push notifications will not work until Firebase credentials are uploaded.
                                </small>
                            </div>
                        )}
                    </AppCard.Body>
                </AppCard>

                {/* Upload Card */}
                <AppCard>
                    <AppCard.Header>
                        <div className="flex flex-col gap-1">
                            <span>{config ? "Update" : "Upload"} Credentials</span>
                            <small className="text-neutral-500 font-normal">
                                Upload a Firebase service account JSON file.
                            </small>
                        </div>
                    </AppCard.Header>
                    <AppCard.Body>
                        <div className="space-y-4">
                            <label
                                className={`flex items-center justify-center gap-2 p-2 border rounded text-sm font-medium transition-colors ${
                                    isUploading
                                        ? "border-gray-300 text-gray-400 cursor-wait"
                                        : "border-blue-300 bg-white text-blue-600 hover:bg-blue-50 cursor-pointer"
                                }`}
                            >
                                <i className={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"} />
                                {isUploading ? "Uploading..." : "Choose JSON File"}
                                <input
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileChange}
                                    disabled={isUploading}
                                    style={{ display: "none" }}
                                />
                            </label>

                            <div className="p-3 bg-blue-50 rounded border border-blue-200">
                                <div className="text-blue-800 text-sm">
                                    <div className="font-semibold mb-1">
                                        <i className="pi pi-info-circle mr-2" />
                                        How to get credentials
                                    </div>
                                    <ol className="list-decimal list-inside space-y-1 text-xs">
                                        <li>Go to Firebase Console &rarr; Project Settings</li>
                                        <li>Navigate to Service Accounts tab</li>
                                        <li>Click &quot;Generate new private key&quot;</li>
                                        <li>Upload the downloaded JSON file here</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </AppCard.Body>
                </AppCard>
            </div>
        </>
    );
};

export default FirebaseConfig;
